import React, { useState, useEffect } from 'react';
import {
    Box,
    VStack,
    Heading,
    Text,
    Button,
    useToast,
    Select,
    FormControl,
    FormLabel,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Badge,
    Input,
    Checkbox,
    Switch,
} from '@chakra-ui/react';
import axiosInstance from "../../../utils/axios";

const UserGroupsSection = ({ userId }) => {
    const [userGroups, setUserGroups] = useState([]);
    const [allGroups, setAllGroups] = useState([]);
    const [selectedGroup, setSelectedGroup] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const [isPrivateGroup, setIsPrivateGroup] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const toast = useToast();

    const fetchUserGroups = async () => {
        try {
            // First get the user details to get the correct id
            const userResponse = await axiosInstance.post(
                `/dummy/get-user`,
                { target_user_id: userId }
            );
            
            if (!userResponse.data || !userResponse.data.data) {
                throw new Error('Invalid user data received');
            }

            const userData = userResponse.data.data;
            const correctUserId = userData.id; // Use the id from user details

            // Now fetch the groups using the correct id
            const response = await axiosInstance.post(
                `/dummy/get-user-groups-per-member`,
                { target_user_id: correctUserId }
            );
            console.log('User groups response:', response.data); // Debug log
            if (response.data) {
                setUserGroups(response.data);
            }
        } catch (error) {
            console.error("Error fetching user groups:", error);
            toast({
                title: "Error",
                description: "Failed to fetch user groups",
                status: "error",
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const fetchAllGroups = async () => {
        try {
            const response = await axiosInstance.post(
                `/dummy/get-user-groups`,
                {
                    searchString: "",
                    activeYN: null,
                    itemsPerPage: 100,
                    currentPage: 1
                }
            );
            if (response.data && response.data.users) {
                setAllGroups(response.data.users);
            }
        } catch (error) {
            console.error('Error fetching all groups:', error);
            toast({
                title: 'Error',
                description: 'Failed to fetch all groups',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    useEffect(() => {
        fetchUserGroups();
        fetchAllGroups();
    }, [userId]);

    const handleAddToGroup = async () => {
        if (!selectedGroup && !newGroupName) {
            toast({
                title: 'Error',
                description: 'Please either select an existing group or enter a new group name',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
            return;
        }

        setIsLoading(true);
        try {
            await axiosInstance.post(
                `/dummy/add-user-usergroup`,
                {
                    user_uuid: userId,
                    target_user_id: userId,
                    husergroupID: selectedGroup || "",
                    new_group_name: newGroupName || "",
                    new_group_privateyn: isPrivateGroup ? "true" : "false"
                }
            );
            
            toast({
                title: 'Success',
                description: 'User added to group successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            
            fetchUserGroups();
            setSelectedGroup('');
            setNewGroupName('');
            setIsPrivateGroup(false);
        } catch (error) {
            console.error('Error adding user to group:', error);
            toast({
                title: 'Error',
                description: 'Failed to add user to group',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleRemoveFromGroup = async (groupId) => {
        setIsLoading(true);
        try {
            await axiosInstance.post(`/dummy/remove-user-from-group`, {
                user_id: userId,
                group_id: groupId
            });
            
            toast({
                title: 'Success',
                description: 'User removed from group successfully',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
            
            fetchUserGroups();
        } catch (error) {
            console.error('Error removing user from group:', error);
            toast({
                title: 'Error',
                description: 'Failed to remove user from group',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleActive = async (allocId, currentStatus) => {
        try {
            console.log('Current status type:', typeof currentStatus, 'Value:', currentStatus);
            const newStatus = currentStatus === true ? false : true;
            console.log('New status:', newStatus);
            
            const response = await axiosInstance.post(
                `/dummy/toggle-member-active`,
                {
                    alloc_h_user_h_usergroup_id: allocId,
                    active_yn: newStatus
                }
            );
            
            console.log('Toggle response:', response);
            
            if (response && response.data) {
                toast({
                    title: 'Success',
                    description: 'Member status updated successfully',
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });
                fetchUserGroups();
            } else {
                throw new Error('Invalid response from server');
            }
        } catch (error) {
            console.error('Error toggling member status:', error);
            toast({
                title: 'Error',
                description: 'Failed to update member status',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    return (
        <Box>
            {/* Current Groups */}
            <Box mb={6}>
                <Text fontWeight="600" mb={2}>Current Groups</Text>
                {userGroups && userGroups.length > 0 ? (
                    <Table variant="simple" size="sm">
                        <Thead>
                            <Tr>
                                <Th>Group Name</Th>
                                <Th>Type</Th>
                                <Th>Private</Th>
                                <Th>Active YN</Th>
                            </Tr>
                        </Thead>
                        <Tbody>
                            {userGroups.map((group, index) => (
                                <Tr key={index}>
                                    <Td>{group.short_desc}</Td>
                                    <Td>{group.group_type}</Td>
                                    <Td>
                                        <Badge colorScheme={group.private_yn ? 'red' : 'green'}>
                                            {group.private_yn ? 'Yes' : 'No'}
                                        </Badge>
                                    </Td>
                                    <Td>
                                        <Switch
                                            isChecked={group.active_yn}
                                            onChange={() => handleToggleActive(group.alloc_h_user_h_usergroup_id, group.active_yn)}
                                            colorScheme="green"
                                        />
                                    </Td>
                                </Tr>
                            ))}
                        </Tbody>
                    </Table>
                ) : (
                    <Text>No groups assigned</Text>
                )}
            </Box>

            {/* Add to Group Form */}
            <Box>
                <Text fontWeight="600" mb={2}>Add to User Group</Text>
                <VStack spacing={4} align="stretch">
                    <FormControl>
                        <FormLabel>Select Existing Group</FormLabel>
                        <Select
                            value={selectedGroup}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            placeholder="Select a group"
                        >
                            {allGroups
                                .filter(group => !userGroups.some(ug => ug.h_usergroup_id === group.h_usergroup_id))
                                .map(group => (
                                    <option key={group.h_usergroup_id} value={group.h_usergroup_id}>
                                        {group.short_desc}
                                    </option>
                                ))}
                        </Select>
                    </FormControl>

                    <FormControl>
                        <FormLabel>New Group</FormLabel>
                        <Input
                            value={newGroupName}
                            onChange={(e) => setNewGroupName(e.target.value)}
                            placeholder="Enter new group name"
                        />
                    </FormControl>

                    <FormControl>
                        <FormLabel>Private Group</FormLabel>
                        <Checkbox
                            isChecked={isPrivateGroup}
                            onChange={(e) => setIsPrivateGroup(e.target.checked)}
                        >
                            Private Group
                        </Checkbox>
                    </FormControl>

                    <Button
                        colorScheme="blue"
                        onClick={handleAddToGroup}
                        isLoading={isLoading}
                        isDisabled={!selectedGroup && !newGroupName}
                    >
                        Add to Group
                    </Button>
                </VStack>
            </Box>
        </Box>
    );
};

export default UserGroupsSection; 